import { memo } from 'react';
import { cn } from '@/lib/utils';
import InboxButton from './inbox-button';
import LabelsManagerButton from './labels-manager-button';
import MarkAsDoneButton from './mark-as-done-button';
import NeedAttentionButton from './need-attention-button';
import NotesButton from './notes-button';
import HandleLaterPopper from './poppers/handle-later-popper';
import RemoveFromLabelButton from './remove-from-label-button';
import StarButton from './star-button';

// Order: Handle Later, Need Attention, Mark as Done, Archive, Star, Labels

interface ContactActionGroupProps {
    waChatId: string;
    showHandleLater?: boolean;
    showNeedAttention?: boolean;
    showMarkAsDone?: boolean;
    showArchive?: boolean;
    showInbox?: boolean;
    showStar?: boolean;
    showLabels?: boolean;
    showNotes?: boolean;

    size?: number; // Icon/Button size
    gap?: number | string; // Gap between items
    className?: string;
}

export default memo(function ContactActionGroup({
    waChatId,
    showHandleLater,
    showNeedAttention,
    showMarkAsDone,
    showInbox,
    showStar,
    showLabels,
    showNotes,
    size = 18,
    gap = 8,
}: ContactActionGroupProps) {

    const gapStyle = typeof gap === 'number' ? `${gap}px` : gap;

    return (
        <div
            className={cn("flex items-center")}
            style={{ gap: gapStyle, padding: '0' }}
        >
            {/* Self-gating: only renders while a real user label is the active view AND this
                contact belongs to it. Placed FIRST so that when you're viewing a label, the
                contextually-primary "remove from this label" action (#162) leads and stays
                visible even on width-constrained chat rows. */}
            <span data-bt-feature="chatRowRemoveFromLabel" style={{ display: 'inline-flex' }}>
                <RemoveFromLabelButton waChatId={waChatId} size={size} />
            </span>
            {showInbox && (
                <span data-bt-feature="chatHoverInbox" style={{ display: 'inline-flex' }}>
                    <InboxButton waChatId={waChatId} size={size} />
                </span>
            )}
            {showNeedAttention && (
                <span data-bt-feature="chatHoverNeedAttention" style={{ display: 'inline-flex' }}>
                    <NeedAttentionButton waChatId={waChatId} size={size} />
                </span>
            )}
            {showMarkAsDone && <MarkAsDoneButton waChatId={waChatId} size={size} />}
            {showHandleLater && (
                <span data-bt-feature="chatHoverHandleLater" style={{ display: 'inline-flex' }}>
                    <HandleLaterPopper waChatId={waChatId} size={size} />
                </span>
            )}
            {showStar && (
                <span data-bt-feature="chatHoverStar" style={{ display: 'inline-flex' }}>
                    <StarButton waChatId={waChatId} size={size} />
                </span>
            )}
            {showNotes && <NotesButton waChatId={waChatId} size={size} />}
            {showLabels && (
                <span data-bt-feature="chatRowLabels" style={{ display: 'inline-flex' }}>
                    <LabelsManagerButton waChatId={waChatId} size={size} />
                </span>
            )}
        </div>
    );
})
